/* =============================================================================
   CORE / CAPACITY — Belegungs- & Verfügbarkeits-Berechnungen
   =============================================================================
   Zweites echtes ES-Modul der Codebase (Phase 1.2). Enthält die Pure-Logic
   rund um Auslastung, Kapazität, Live-Verfügbarkeit für das Customer-Form
   und die 7-Tages-Prognose fürs Admin-Cockpit.

   State-Zugriff:
     Alle Funktionen lesen den globalen App-State (Reservierungen, Tische,
     Settings) bewusst lazy über `window.App.storage._getInternalState()`,
     statt ihn als Parameter durchzureichen. Damit bleiben die Aufruf-
     Signaturen unverändert zum bisherigen Code — wichtig, weil die
     gleichen Functions in app.js und in inline-onclick-Handlern aufgerufen
     werden.

   Externe Dependencies (jetzt im `core/reservation-engine.js`-Modul):
     - findTableAssignment(date, time, guests, area)
     - generateTimeSlots()
   Werden **bewusst** lazy via `window.*` aufgelöst, NICHT per `import`.
   Begründung: `reservation-engine` importiert seinerseits aus `capacity`
   (durations, totalCapacity, bookedSeatsAt …). Ein direkter Import hier
   würde Circular Imports erzeugen. Die window-Indirektion bricht den
   Kreis ohne Verhaltens-Konsequenzen.

   Backward-Compat: alle exportierten Functions werden zusätzlich ans
   `window` gehängt — solange app.js diese Functions noch identisch lokal
   definiert hat (Übergangs-Duplikat), gewinnt die später ausgeführte
   Definition; da beide identisch sind, kein Konflikt.
============================================================================ */

import { timeToMinutes, dateToISO } from '../utils/helpers.js';
// S271 §A — Walk-In-Peak (Sweep) teilt sich die Event-Mechanik mit den
// Reservierungen: walkInEventsInWindow/peakOfEvents sind die eine Quelle.
import { effectiveOccupancyEndMs, walkInEventsInWindow, peakOfEvents } from './table-occupancies.js';
// S207 HOTFIX 5 — Single-Source-Formel für die Max-Kapazität eines Tisches
// (seats + extendable?extra_chairs:0), dieselbe wie Kachel-„XP"/tableMaxPersons.
// table-plan.js ist window-frei/pure → kein Circular Import (anders als
// reservation-engine, das lazy via window aufgelöst wird).
import { tableMaxPersons } from './table-plan.js';

// -----------------------------------------------------------------------------
// Helper: aktueller State (lazy)
// -----------------------------------------------------------------------------
function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

// -----------------------------------------------------------------------------
// Reservierungs-Dauer & Slot-Settings
// -----------------------------------------------------------------------------

/** Dauer einer einzelnen Reservierung in Minuten — Fallback aus Settings. */
export function getReservationDuration(r) {
  const state = getState();
  const d = Number(r && r.durationMin);
  if (Number.isFinite(d) && d > 0) return d;
  const fb = Number(state.settings && state.settings.slotDurationMin);
  return Number.isFinite(fb) && fb > 0 ? fb : 120;
}

/** Default-Reservierungsdauer aus Settings (Fallback 120 Min). */
export function getCurrentSlotDuration() {
  const state = getState();
  const fb = Number(state.settings && state.settings.slotDurationMin);
  return Number.isFinite(fb) && fb > 0 ? fb : 120;
}

/** S134 P3 — Minuten-seit-Tagesbeginn eines Freigabe-Zeitstempels (relativ zum
 *  Reservierungs-Datum). null bei ungültigem Wert. */
function releasedAtToMinutes(iso, dateStr) {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  const base = new Date(dateStr + 'T00:00:00').getTime();
  if (isNaN(base)) return null;
  return Math.round((t - base) / 60000);
}

/** S134 P3 — effektives Block-Ende einer Reservierung in Minuten. Standard:
 *  start + Verweildauer. Wurde der Tisch nach dem Kassieren vorzeitig
 *  freigegeben (`tableReleasedAt`), endet die Blockade dann — die geplante
 *  Dauer im Datensatz bleibt unangetastet. */
export function effectiveReservationEndMin(r, startMin) {
  const plannedEnd = startMin + getReservationDuration(r);
  const rel = r && r.tableReleasedAt;
  if (rel) {
    const relMin = releasedAtToMinutes(rel, r.date);
    if (relMin != null && relMin < plannedEnd) return Math.max(startMin, relMin);
  }
  return plannedEnd;
}

/** Zwei Reservierungen am gleichen Tag, überschneiden sich zeitlich?
 *  Berücksichtigt vorzeitige Tisch-Freigabe (S134 P3). */
export function reservationOverlaps(resA, resB) {
  if (resA.date !== resB.date) return false;
  const aStart = timeToMinutes(resA.time);
  const aEnd   = effectiveReservationEndMin(resA, aStart);
  const bStart = timeToMinutes(resB.time);
  const bEnd   = effectiveReservationEndMin(resB, bStart);
  return aStart < bEnd && bStart < aEnd;
}

// -----------------------------------------------------------------------------
// Tisch-Filter & Kapazität
// -----------------------------------------------------------------------------

/**
 * S239 Cut A0 — IDs der Reservierungen, die bereits eingecheckt sind („Gäste
 * sind da"): es gibt eine AKTIVE Belegung mit dieser `reservationId`.
 *
 * Solange die Reservierung nur gebucht ist, blockt sie den Tisch (Säule 1).
 * Nach dem Check-in ist die Belegung die Wahrheit über den Tisch — Sitzdauer
 * läuft ab `started_at`, Überfällig-Uhr und Auto-Release greifen, und der Tisch
 * darf NICHT zweimal (Reservierung + Belegung) als belegt gezählt werden.
 * Pure bis auf den State-Read — Test-Hook.
 */
export function checkedInReservationIds(state) {
  const ids = new Set();
  for (const o of ((state && state.tableOccupancies) || [])) {
    if (!o || o.deletedAt || o.status !== 'active') continue;
    if (o.reservationId) ids.add(o.reservationId);
  }
  return ids;
}

/** S60 W4.13 — Tisch-Sharing-State für Walk-Ins.
 *  Liefert für einen Tisch + Zeitfenster den Belegungs-Zustand inkl. freier Plätze.
 *  Bei Reservierung: voll blockiert (Privatsphäre, kein Sharing).
 *  Bei nur Walk-Ins: shareable, freeSeats = table.seats − Σ walkin.guests.
 *  @returns { occupiedSeats, freeSeats, walkIns: [], hasReservation: bool, isFullyBlocked: bool }
 */
export function getTableSharingState(tableId, date, time, durationMin) {
  const state = getState();
  const table = (state.tables || []).find(t => t.id === tableId);
  const tableSeats = (table && table.seats) || 0;
  const probe = { date, time, durationMin: Number(durationMin) || getCurrentSlotDuration() };
  const probeStart = timeToMinutes(time);
  const probeEnd   = probeStart + probe.durationMin;
  let hasReservation = false;
  let occupiedSeats = 0;
  const walkIns = [];
  // S239 Cut A0 — eingecheckte Reservierungen („Gäste sind da"): ab dem Check-in
  // hält die BELEGUNG den Tisch, nicht mehr der Reservierungs-Block. Sonst würde
  // derselbe Tisch doppelt gehalten (Säule 1 + Säule 2) und die Kachel bliebe im
  // Reservierungs-Zustand stehen — ohne Überfällig-Uhr, ohne „Rechnung offen".
  const checkedInResIds = checkedInReservationIds(state);
  // Säule 1: Reservation (exklusiv)
  for (const r of state.reservations) {
    if (r.deletedAt) continue;
    if (r.status === 'declined' || r.status === 'cancelled') continue;
    if (!(r.assignedTables || []).includes(tableId)) continue;
    if (!reservationOverlaps(probe, r)) continue;
    if (checkedInResIds.has(r.id)) continue;   // → zählt unten als Belegung
    hasReservation = true;
    occupiedSeats = tableSeats; // Reservation blockiert IMMER ganzen Tisch
    break;
  }
  // Säule 2: Walk-Ins (shareable) — Mitternachts-Hotfix: zeitfenster-basiert
  // statt nur day-key, damit Walk-Ins die über Mitternacht laufen am Folgetag
  // korrekt erfasst werden.
  if (!hasReservation) {
    const dayStartMs = new Date(date + 'T00:00:00').getTime();
    const probeStartMs = dayStartMs + probeStart * 60 * 1000;
    const probeEndMs   = dayStartMs + probeEnd * 60 * 1000;
    for (const o of (state.tableOccupancies || [])) {
      if (o.deletedAt || o.status !== 'active' || !o.startedAt) continue;
      const matchTable = o.tableId === tableId
        || (Array.isArray(o.assignedTableIds) && o.assignedTableIds.includes(tableId));
      if (!matchTable) continue;
      const startMs = new Date(o.startedAt).getTime();
      if (isNaN(startMs)) continue;
      // S196 Cut a — aktive Belegung blockt bis max(geplant, jetzt): eine
      // überzogene Belegung bleibt belegt, bis sie freigegeben/auto-freigegeben wird.
      const endMs = effectiveOccupancyEndMs(o);
      if (isNaN(endMs)) continue;
      if (startMs < probeEndMs && endMs > probeStartMs) {
        walkIns.push(o);
        occupiedSeats += (Number(o.guests) || 0);
      }
    }
  }
  const freeSeats = Math.max(0, tableSeats - occupiedSeats);
  const isFullyBlocked = hasReservation || freeSeats <= 0;
  return { occupiedSeats, freeSeats, walkIns, hasReservation, isFullyBlocked, tableSeats };
}

/** IDs der zum Slot belegten Tische (Set), optional eine eigene ID ausschließen.
 *  S60 W2 — 3-Pfad-Aggregation: berücksichtigt sowohl `reservations` (Säule 1)
 *  als auch aktive `table_occupancies` (Säule 2, Walk-Ins).
 *  Ein Tisch gilt als „blockiert" wenn er VOLL belegt ist (siehe getTableSharingState
 *  für die nuancierte Sharing-Logik). */
export function getOccupiedTableIds(date, time, durationMin, excludeId) {
  const state = getState();
  const probe = { date, time, durationMin: Number(durationMin) || getCurrentSlotDuration() };
  const probeStart = timeToMinutes(time);
  const probeEnd   = probeStart + probe.durationMin;
  const occupied = new Set();
  // S239 Cut A0: solange der Check-in AKTIV ist, hält die Belegung den Tisch
  // (Säule 2) — dieselbe Wahrheit wie in getTableSharingState. Der Set würde die
  // Tisch-ID zwar ohnehin nur einmal führen; entscheidend ist, dass beide
  // Funktionen denselben Halter sehen (Belegung überzieht → bleibt belegt;
  // Belegung beendet → der Reservierungs-Block greift wieder wie bisher, bis
  // `table_released_at` gesetzt ist).
  const checkedInResIdsB = checkedInReservationIds(state);
  // Säule 1: Reservations (Voraus-Buchungen)
  for (const r of state.reservations) {
    if (excludeId && r.id === excludeId) continue;
    if (r.deletedAt) continue;
    if (checkedInResIdsB.has(r.id)) continue;
    if (r.status === 'declined' || r.status === 'cancelled') continue;
    if (!reservationOverlaps(probe, r)) continue;
    (r.assignedTables || []).forEach(tid => occupied.add(tid));
  }
  // Säule 2: Walk-Ins — Mitternachts-Hotfix: zeitfenster-basiert.
  const dayStartMsB = new Date(date + 'T00:00:00').getTime();
  const probeStartMsB = dayStartMsB + probeStart * 60 * 1000;
  const probeEndMsB   = dayStartMsB + probeEnd * 60 * 1000;
  for (const o of (state.tableOccupancies || [])) {
    if (excludeId && o.id === excludeId) continue;
    if (o.deletedAt) continue;
    if (o.status !== 'active') continue;
    if (!o.startedAt) continue;
    const startMs = new Date(o.startedAt).getTime();
    if (isNaN(startMs)) continue;
    // S196 Cut a — effektives Ende = max(geplant, jetzt) für aktive Belegungen.
    const endMs = effectiveOccupancyEndMs(o);
    if (isNaN(endMs)) continue;
    if (startMs < probeEndMsB && endMs > probeStartMsB) {
      if (o.tableId) occupied.add(o.tableId);
      if (Array.isArray(o.assignedTableIds)) o.assignedTableIds.forEach(tid => occupied.add(tid));
    }
  }
  return occupied;
}

/**
 * Tische, die für eine Reservierung in Frage kommen.
 * Filtert out: dauerhaft gesperrte Tische, deaktivierte Bereiche,
 * Tische außerhalb des gewählten Bereichs (falls vorgegeben).
 */
export function getEligibleTables(area) {
  const state = getState();
  let tables = state.tables.slice();
  tables = tables.filter(t => t.disabled !== true);
  if (!state.settings.outdoorEnabled) tables = tables.filter(t => t.area !== 'outdoor');
  if (!state.settings.clubEnabled)    tables = tables.filter(t => t.area !== 'club');
  if (area && area !== 'any') tables = tables.filter(t => t.area === area);
  return tables;
}

/** Zusatzstühle pro Tisch (positive Number, sonst 0). */
export function tableExtraChairs(t) {
  const v = Number(t && t.extraChairs);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/** Σ Max-Kapazität aller eligible Tische über alle Bereiche.
 *  S207 HOTFIX 5 (Jonas-Entscheid) — inkl. hinterlegter Zusatzstühle (nur bei
 *  kombinierbaren Tischen), nicht mehr nur feste Sitzplätze. Deckungsgleich mit
 *  dem Server-Gate (Trigger enforce_reservation_capacity, phase_155) und der
 *  Kachel-„XP". Die Spontan-Reserve wird davon abgezogen (spontaneousReserveSeats). */
export function totalCapacity() {
  return getEligibleTables('any').reduce((s, t) => s + tableMaxPersons(t), 0);
}

/** Anzahl Plätze, die als Spontan-Reserve frei bleiben (gerundet). */
export function spontaneousReserveSeats() {
  const state = getState();
  return Math.ceil(totalCapacity() * ((state.settings.spontaneousReservePercent || 0) / 100));
}

// -----------------------------------------------------------------------------
// Tages-Sittings (Auslastung)
// -----------------------------------------------------------------------------

/**
 * Maximale Personen-Sittings pro Tag.
 *   = Sitzplätze × (Service-Min / Standard-Reservierungsdauer)
 * Beispiel: 58 Plätze × (720 Min / 90 Min) = 58 × 8 = 464.
 *
 * Damit "frei werdende" Tische in die Auslastung einfließen: Ein Tisch,
 * der über den Tag mehrmals belegt wird, zählt mehrfach in der Kapazität.
 */
export function getDayCapacityUnits() {
  const state = getState();
  const cap = totalCapacity();
  if (!cap) return 0;
  const startMin = timeToMinutes(state.settings.openingTimeStart || '11:00');
  const endMin   = timeToMinutes(state.settings.openingTimeEnd   || '23:00');
  const serviceMin = Math.max(60, endMin - startMin);
  const slotDur = getCurrentSlotDuration();
  const sittings = Math.max(1, serviceMin / slotDur);
  return cap * sittings;
}

/**
 * Personen-Sittings, die an einem Tag belegt sind.
 *   = Σ (Gäste × Dauer/SlotDur) aller aktiven Reservierungen am Tag.
 */
export function getDayUsedUnits(dateStr) {
  const state = getState();
  const slotDur = getCurrentSlotDuration();
  let used = 0;
  for (const r of state.reservations) {
    if (r.date !== dateStr) continue;
    // Nur bestätigte Buchungen — konsistent mit Peak/Cap-Regel.
    if (!countsTowardCapacity(r)) continue;
    const dur = getReservationDuration(r);
    used += r.guests * (dur / slotDur);
  }
  return used;
}

/** Auslastung eines Tages als Fraktion (0..1+). Kann >1 sein bei Überbuchung. */
export function computeDayUtilization(dateStr) {
  const max = getDayCapacityUnits();
  if (!max) return 0;
  return getDayUsedUnits(dateStr) / max;
}

/**
 * Σ Gäste, die exakt zum Zeitpunkt `time` simultan anwesend sind
 * (Reservierungsstart ≤ time < Reservierungsende). O(N).
 *
 * Wird vom Admin-Cockpit für den Peak-Wert genutzt — die Frage lautet hier:
 * "Wie voll ist das Restaurant zu DIESER Uhrzeit?", nicht "wieviele Gäste
 * kommen in den nächsten 2h irgendwann vorbei?".
 */
export function simultaneousGuestsAt(date, time, excludeId) {
  const state = getState();
  const t = timeToMinutes(time);
  let count = 0;
  for (const r of state.reservations) {
    if (excludeId && r.id === excludeId) continue;
    if (r.date !== date) continue;
    // Nur bestätigte Buchungen (confirmed/completed) zählen — pending
    // ist eine offene Anfrage, kann noch abgelehnt werden, darf den Cap
    // nicht reservieren. Gleiche Regel wie der DB-Trigger.
    if (!countsTowardCapacity(r)) continue;
    const rStart = timeToMinutes(r.time);
    const rEnd   = rStart + getReservationDuration(r);
    if (rStart <= t && t < rEnd) count += r.guests;
  }
  // S271 §A — Walk-Ins sitzen physisch am Tisch: zum Zeitpunkt t anwesende
  // echte Walk-Ins (ohne reservationId) zählen mit — Peak im Kapazitäts-Cockpit
  // und Gast-Verfügbarkeit sehen damit dieselbe Belegung.
  count += walkInSeatsInWindow(date, t, t + 1, excludeId);
  return count;
}

/**
 * S271 §A — Spitzenwert gleichzeitig anwesender Walk-In-Gäste im Fenster
 * [startMin, endMin) (nur echte Walk-Ins ohne reservationId; eingecheckte
 * Reservierungen zählen über die Reservierung). Mitternachts-sicher.
 */
export function walkInSeatsInWindow(date, startMin, endMin, excludeId) {
  const state = getState();
  return peakOfEvents(walkInEventsInWindow(date, startMin, endMin, state.tableOccupancies || [], { excludeId }));
}

/** Walk-In-Peak für die Reservierungsdauer ab `time` (Gegenstück zu bookedSeatsAt).
 *  S272-B: `durationMin` = Dauer der konkreten Reservierung; ohne Angabe die
 *  Standard-Reservierungsdauer (settings.slotDurationMin). */
export function walkInSeatsAt(date, time, excludeId, durationMin) {
  const startMin = timeToMinutes(time);
  return walkInSeatsInWindow(date, startMin, startMin + effectiveDuration(durationMin), excludeId);
}

/** S272-B — Fenster-Dauer: konkrete Reservierungsdauer, sonst Standard. */
export function effectiveDuration(durationMin) {
  const d = Number(durationMin);
  return Number.isFinite(d) && d > 0 ? d : getCurrentSlotDuration();
}

/**
 * S271 §A — physisch belegte Plätze im Fenster: Reservierungen (bestätigt)
 * UND Walk-Ins in EINEM Sweep. Das ist die harte Grenze gegen totalCapacity()
 * (Cockpit-Walk-In, Bestätigen) — Spiegel von enforce_reservation_capacity.
 */
export function occupiedSeatsInWindow(date, startMin, endMin, excludeId) {
  const state = getState();
  const events = _reservationEvents(state, date, startMin, endMin, excludeId)
    .concat(walkInEventsInWindow(date, startMin, endMin, state.tableOccupancies || [], { excludeId }));
  return peakOfEvents(events);
}

/**
 * S271 §A — EINE Definition der freien Gast-Plätze (Jonas-Regel 07.10.):
 * Walk-Ins belegen zuerst die Spontan-Reserve; übersteigen sie die Reserve,
 * sinkt die Gast-Verfügbarkeit um den Überschuss.
 *
 *   free = totalCap − bookedSeats(Reservierungen) − max(reserve, walkInSeats)
 *
 * Genutzt von previewAvailability (Gastformular) und evaluateReservation
 * (Engine). Unter der Reserve ändert sich nichts gegenüber vorher.
 */
export function freeSeatsAt(date, time, excludeId, durationMin) {
  // S272-B: das Fenster ist die Dauer der konkreten Reservierung (Cockpit:
  // getReservationDuration(r); Gastformular: Standard-Reservierungsdauer, die
  // es auch abschickt). Vorher immer getCurrentSlotDuration() — bei einer
  // 120-Min-Reservierung und 60-Min-Standard wurde 17:00 angeboten, obwohl
  // 18:00–19:00 voll war (S271-Retest 4.4).
  const dur      = effectiveDuration(durationMin);
  const totalCap = totalCapacity();
  const reserve  = spontaneousReserveSeats();
  const booked   = bookedSeatsAt(date, time, excludeId, dur);
  const walkIn   = walkInSeatsAt(date, time, excludeId, dur);
  const reserveUsed = Math.max(reserve, walkIn);
  const free = Math.max(0, totalCap - booked - reserveUsed);
  return { totalCap, reserve, booked, walkIn, reserveUsed, free, durationMin: dur };
}

/** Sweep-Events der bestätigten Reservierungen im Fenster (intern, geclippt). */
function _reservationEvents(state, date, startMin, endMin, excludeId) {
  const events = [];
  if (!(endMin > startMin)) return events;
  for (const r of state.reservations) {
    if (excludeId && r.id === excludeId) continue;
    if (r.date !== date) continue;
    // Nur bestätigte Buchungen (confirmed/completed) — identisch zum DB-Trigger.
    if (!countsTowardCapacity(r)) continue;
    const rStart = timeToMinutes(r.time);
    const rEnd   = rStart + getReservationDuration(r);
    if (rEnd <= startMin || rStart >= endMin) continue; // kein Overlap
    events.push([Math.max(rStart, startMin), r.guests]);
    events.push([Math.min(rEnd, endMin),  -r.guests]);
  }
  return events;
}

/**
 * Maximale simultan-anwesende Gästezahl, die in einem Fenster [startMin, endMin)
 * auftritt — als Sweep-Line über Start-/End-Events. O(N log N).
 *
 * Wird vom Customer-Form, evaluateReservation und previewAvailability genutzt:
 * "Wenn ich ab T für D Minuten buche, wie voll ist der vollste Augenblick
 * während meiner Anwesenheit?". Genau diese Zahl plus die neue Gruppe darf
 * die Kapazität nicht überschreiten.
 *
 * Wichtig: zählt NICHT die Gesamt-Gäste mit Überlappung (das wäre der alte
 * Bug — überzählt sequenzielle Buchungen).
 */
export function maxSimultaneousInWindow(date, startMin, endMin, excludeId) {
  // S271 §A: Event-Sammlung + Peak sind geteilt (peakOfEvents: End-Events vor
  // Start-Events bei gleichem Zeitpunkt — kein künstlicher Peak an Slot-Grenzen).
  return peakOfEvents(_reservationEvents(getState(), date, startMin, endMin, excludeId));
}

/**
 * Σ belegte Plätze für die geplante Reservierungs-Dauer ab `time`.
 * Nutzt jetzt maxSimultaneousInWindow statt naive Overlap-Summe — sonst
 * werden sequenzielle Reservierungen (z.B. 17:30-19:30 + 19:30-21:30)
 * doppelt gezählt, obwohl sie sich nicht überlappen.
 */
export function bookedSeatsAt(date, time, excludeId, durationMin) {
  const startMin = timeToMinutes(time);
  const endMin   = startMin + effectiveDuration(durationMin);   // S272-B
  return maxSimultaneousInWindow(date, startMin, endMin, excludeId);
}

/** Wird die Reservierung als "aktiv" gezählt (für Listen, KPIs, Pipeline-Sicht)?
 *  Schließt nur tote Stati aus — pending bleibt aktiv für Listen-Anzeigen. */
export function isActiveReservation(r) {
  // S132c — `waitlist` abgeschafft (phase_99); kein Sonderzweig mehr nötig.
  return r.status !== 'declined' && r.status !== 'cancelled' &&
         r.status !== 'no-show';
}

/**
 * S116 — Terminal-Prädikat für „Als Nächstes" + Erledigt-Zähler (Single-Source).
 * Eine Reservierung ist „erledigt" (fällt aus der operativen Sicht), wenn sie
 * abgesagt/storniert/no-show/Warteliste ODER explizit `completed` (S116-Abschluss)
 * ist. Bewusst NICHT `isActiveReservation` wiederverwendet: dort zählt `completed`
 * weiter als belegt (countsTowardCapacity) → Kapazität nutzt weiter isActive-
 * Reservation, die OPERATIVE Sicht nutzt isReservationDone. `pending`/`confirmed`
 * sind NICHT im Set → bleiben offen.
 */
const RESERVATION_DONE = new Set(['declined', 'cancelled', 'no-show', 'completed']);
export function isReservationDone(reservation) {
  return RESERVATION_DONE.has(reservation && reservation.status);
}

/**
 * Strengeres Predikat: zählt diese Reservierung TATSÄCHLICH gegen die
 * physische Kapazität? Nur `confirmed` und `completed`.
 *
 * Begründung (Nutzerregel): pending = noch nicht bestätigt, könnte
 * abgelehnt werden — darf den Cap also nicht reservieren. waitlist hat
 * kein Tisch-Setup. no-show, declined, cancelled sprechen für sich.
 *
 * Wird in simultaneousGuestsAt, maxSimultaneousInWindow und
 * getDayUsedUnits genutzt → Peak, Verfügbarkeits-Check und Tages-
 * Auslastung sind alle konsistent.
 */
export function countsTowardCapacity(r) {
  return r.status === 'confirmed' || r.status === 'completed';
}

/**
 * Visuelle Variante derselben Frage: "Soll diese Reservierung im Tisch-
 * Render als BESTÄTIGT (grün) erscheinen oder als offen (orange)?"
 *
 * Identisch zu `countsTowardCapacity`. Wir behalten sie als eigenen
 * Helper, damit Färbung (Timeline/Snapshot) und Zählung (Peak/Cap-Check)
 * nicht versehentlich auseinanderlaufen — Inkonsistenz hier war die
 * Ursache für die "55 vs. 59"-Diskrepanz im Belegungs-Cockpit
 * (orange-gefärbte `completed`-Reservierungen wurden trotzdem mitgezählt).
 */
export function isConfirmedSlot(r) {
  return countsTowardCapacity(r);
}

/**
 * Würde eine Reservierung (Start + Dauer) über die Schließzeit hinausgehen?
 * Liefert null bei OK, sonst ein Objekt mit closeMin/endMin/end für UI-Hinweise.
 * Zentrale Wahrheit: wird vom Customer-Form, Walk-In, evaluateReservation und
 * previewAvailability genutzt. Backend hat einen Trigger als Sicherheitsnetz.
 */
export function violatesClosingTime(time, durationMin) {
  const state = getState();
  if (!time) return null;
  const closeStr = state.settings && state.settings.openingTimeEnd;
  if (!closeStr) return null; // keine Schließzeit gesetzt → kein Check
  const dur = Number(durationMin) > 0 ? Number(durationMin) : getCurrentSlotDuration();
  const startMin = timeToMinutes(time);
  const endMin   = startMin + dur;
  const closeMin = timeToMinutes(closeStr);
  if (endMin <= closeMin) return null;
  return {
    closeMin,
    endMin,
    closeStr,
    endStr: String(Math.floor(endMin / 60) % 24).padStart(2, '0') + ':' + String(endMin % 60).padStart(2, '0'),
    overrunMin: endMin - closeMin
  };
}

// -----------------------------------------------------------------------------
// Live-Verfügbarkeit & Alternativen (Customer-Form)
// -----------------------------------------------------------------------------

/**
 * Prüft, ob der Slot (Datum/Zeit/Personen/Bereich) buchbar ist und liefert
 * Status (free|tight|full|closed) plus Begründung und potenzielle Tisch-
 * Zuweisung. Wird live im Customer-Form aufgerufen, bei jeder Änderung.
 */
export function previewAvailability(date, time, guests, area, opts) {
  const state = getState();
  if (!date || !time) return { status: 'unknown', canBook: false };
  // S272-B: opts = { durationMin, excludeId } — Fenster = Dauer der konkreten
  // Reservierung (Cockpit-Modal) bzw. Standard-Reservierungsdauer (Gast).
  opts = opts || {};
  const durationMin = effectiveDuration(opts.durationMin);
  // 1) Öffnungszeiten
  const tMin = timeToMinutes(time);
  if (tMin < timeToMinutes(state.settings.openingTimeStart) ||
      tMin >= timeToMinutes(state.settings.openingTimeEnd)) {
    return { status: 'closed', canBook: false, reason: 'Außerhalb der Öffnungszeiten.' };
  }
  // 1b) Reservierungs-Ende muss innerhalb der Öffnungszeit liegen.
  const overrun = violatesClosingTime(time, durationMin);
  if (overrun) {
    return {
      status: 'closed',
      canBook: false,
      reason: `Reservierung würde um ${overrun.endStr} enden — nach Ladenschluss um ${overrun.closeStr}.`
    };
  }
  // 2) Bereich aktiviert?
  if (area === 'outdoor' && !state.settings.outdoorEnabled) {
    return { status: 'closed', canBook: false, reason: 'Außenbereich ist gerade nicht buchbar.' };
  }
  if (area === 'club' && !state.settings.clubEnabled) {
    return { status: 'closed', canBook: false, reason: 'Clubraum ist gerade nicht buchbar.' };
  }
  // 3) Kapazitätsberechnung — S271 §A: eine Quelle (freeSeatsAt), Walk-Ins
  //    über der Spontan-Reserve mindern die Gast-Verfügbarkeit.
  const { totalCap, reserve, booked, walkIn, reserveUsed, free } = freeSeatsAt(date, time, opts.excludeId, durationMin);

  const numGuests = parseInt(guests, 10) || 0;
  // 4) Tisch-Zuweisung möglich? (Externe Engine — noch in app.js, daher window.*)
  let assignment = null;
  if (numGuests > 0 && typeof window.findTableAssignment === 'function') {
    assignment = window.findTableAssignment(date, time, numGuests, area || 'any');
  }

  let status, canBook = true, reason;
  if (free <= 0) {
    status = 'full';
    canBook = false;
    reason = 'Dieser Zeitraum ist bereits voll ausgelastet.';
  } else if (numGuests > 0 && numGuests > free) {
    status = 'full';
    canBook = false;
    reason = `Nur noch ${free} Plätze frei — für ${numGuests} Personen zu wenig.`;
  } else if (numGuests > 0 && !assignment) {
    status = 'full';
    canBook = false;
    reason = 'Keine passende Tisch-Kombination verfügbar.';
  } else if (free <= Math.max(2, Math.round(totalCap * 0.10))) {
    status = 'tight';
  } else {
    status = 'free';
  }
  return {
    status, canBook, reason,
    seatsAvailable: free,
    totalCapacity: totalCap,
    reservedSeats: reserve,
    bookedSeats: booked,
    walkInSeats: walkIn,
    reserveUsed,
    durationMin,
    assignment
  };
}

/**
 * Liefert bis zu N alternative Zeitslots am gleichen Tag, an denen die
 * gewünschte Personenzahl Platz hätte. Sortiert nach Nähe zur Wunsch-Zeit.
 */
export function suggestAlternativeTimes(date, wishedTime, guests, area, maxN, opts) {
  maxN = maxN || 4;
  // S272-B: opts = { durationMin, excludeId } wie previewAvailability — eine
  // Alternative gilt nur, wenn die GANZE Reservierungsdauer dort Platz hat.
  opts = opts || {};
  // generateTimeSlots lebt noch in app.js — über window aufrufen.
  const slots = (typeof window.generateTimeSlots === 'function')
    ? window.generateTimeSlots()
    : [];
  const wishMin = timeToMinutes(wishedTime);
  const candidates = [];
  for (const s of slots) {
    if (s === wishedTime) continue;
    const pa = previewAvailability(date, s, guests, area, opts);
    if (pa.canBook) {
      candidates.push({
        time: s,
        distance: Math.abs(timeToMinutes(s) - wishMin),
        seatsAvailable: pa.seatsAvailable,
        status: pa.status
      });
    }
  }
  candidates.sort((a, b) => a.distance - b.distance);
  return candidates.slice(0, maxN);
}

/**
 * 7-Tages-Prognose der Auslastung pro Tag — für das Admin-Cockpit.
 * Return-Shape: [{date, dateLabel, reservations, guests, peakSlot,
 *   peakBooked, capacity, dayUtilization, peakUtilization, status}]
 */
export function getCapacityForecast(days) {
  const state = getState();
  days = days || 7;
  const result = [];
  const cap = totalCapacity();
  const dayCap = Math.round(getDayCapacityUnits());
  const slots = (typeof window.generateTimeSlots === 'function')
    ? window.generateTimeSlots()
    : [];
  for (let i = 0; i < days; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = dateToISO(d); // S61 FIX.4: TZ-konsistent statt UTC-Slice
    // Konsistent mit Peak/Cap-Regel: nur bestätigte Buchungen werden in
    // den Tages-Metriken (Gäste, Auslastung) und im Peak-Sampling
    // berücksichtigt — pending werden ignoriert.
    const dayRes = state.reservations.filter(r => r.date === iso && countsTowardCapacity(r));
    const dayGuests = dayRes.reduce((s, r) => s + r.guests, 0);
    let peakSlot = null, peakBooked = 0;
    // Peak = simultaner Spitzenwert. Sample-Punkte: jede Buchungs-Startzeit
    // PLUS die generierten Slots (für Leerphasen). Reine Slot-Iteration
    // würde späte Buchungen verfehlen, weil generateTimeSlots() nur bis
    // (Schließung − Dauer) reicht — eine Buchung um 21:00 wäre damit nie
    // ein Sample-Punkt. Buchungs-Startzeiten sind exakt die Momente, an
    // denen der simultane Wert steigen kann, daher reichen die.
    const samplePoints = new Set(slots);
    for (const r of dayRes) samplePoints.add(r.time);
    // S271 §A — Walk-In-Startzeiten (lokal) als Sample-Punkte, sonst verfehlt
    // der Peak einen Walk-In zwischen zwei Slots.
    const dayStartMs = new Date(iso + 'T00:00:00').getTime();
    for (const o of (state.tableOccupancies || [])) {
      if (!o || o.status !== 'active' || o.deletedAt || o.reservationId || !o.startedAt) continue;
      const startMs = new Date(o.startedAt).getTime();
      if (isNaN(startMs) || startMs < dayStartMs || startMs >= dayStartMs + 86400000) continue;
      const m = Math.floor((startMs - dayStartMs) / 60000);
      samplePoints.add(String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'));
    }
    for (const time of samplePoints) {
      const booked = simultaneousGuestsAt(iso, time);
      if (booked > peakBooked) {
        peakBooked = booked;
        peakSlot = time;
      }
    }
    const dayUtilFrac = computeDayUtilization(iso);
    const utilization = Math.round(dayUtilFrac * 100);
    const peakUtil = cap ? peakBooked / cap : 0;
    let status = 'free';
    if (dayUtilFrac >= 0.85 || peakUtil >= 1.0)      status = 'critical';
    else if (dayUtilFrac >= 0.60 || peakUtil >= 0.95) status = 'busy';
    else if (dayUtilFrac > 0.05 || peakUtil > 0)     status = 'normal';
    result.push({
      date: iso,
      dateLabel: d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }),
      reservations: dayRes.length,
      guests: dayGuests,
      peakSlot,
      peakBooked,
      // Per-Slot-Kapazität (gleichzeitig sitzende Gäste) — Basis fürs Peak-Anzeigen.
      capacity: cap,
      // Tageskapazität = Plätze × Sittings — Basis für die Tages-Auslastung.
      // Berücksichtigt, dass Tische über den Tag mehrmals belegt werden.
      dayCapacity: dayCap,
      dayUtilization: utilization,
      peakUtilization: Math.round(peakUtil * 100),
      status
    });
  }
  return result;
}

// -----------------------------------------------------------------------------
// Backward-Compat: ans window für inline-onclick-Handler und das (noch)
// klassische app.js. Identische Symbole — kein Verhaltens-Konflikt.
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    getReservationDuration,
    getCurrentSlotDuration,
    reservationOverlaps,
    getOccupiedTableIds,
    getEligibleTables,
    tableExtraChairs,
    totalCapacity,
    spontaneousReserveSeats,
    getDayCapacityUnits,
    getDayUsedUnits,
    computeDayUtilization,
    bookedSeatsAt,
    isActiveReservation,
    isReservationDone,
    previewAvailability,
    suggestAlternativeTimes,
    getCapacityForecast,
    violatesClosingTime,
    simultaneousGuestsAt,
    maxSimultaneousInWindow,
    countsTowardCapacity
  });
}
